import { Link, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import useAuth from "../../hooks/useAuth";
import { useLocalizedPath } from "../../hooks/useLocale";

import MenuLink from "./MenuLink";

import { canAccessAdminDashboard, canImportEquipment, canManageSites, canManageUsers } from "../../utils/roles";

// Public assets are served from the web root — reference by URL, don't import them.
const logo = "/tereos_white.png";
import ProfileIcon from "../icons/Profile";
import LogoutIcon from "../icons/Logout";
import LogoMini from "../icons/LogoMini";
import DashboardIcon from "../icons/Dashboard";
import EquipmentIcon from "../icons/Equipment";
import UsersIcon from "../icons/Users";
import LinkIcon from "../icons/Link";
import SettingIcon from "../icons/Setting";
import DownloadIcon from "../icons/Download";

const SideMenu = () => {
  const { user, removeAuth, isAdmin } = useAuth();
  const navigateTo = useNavigate();
  const localizedPath = useLocalizedPath();
  const intl = useIntl();

  if (!user) {
    navigateTo(localizedPath("/auth"));
    throw new Error(intl.formatMessage({ id: "common.operationNotAllowed" }));
  }

  const showManagementMenu = canAccessAdminDashboard(user.role);
  const showUsersMenu = canManageUsers(user.role);
  const showSitesMenu = canManageSites(user.role);
  const showImportTemplateMenu = canImportEquipment(user.role);

  return (
    <div
      className="px-4 pt-6 pb-10 h-full text-white flex flex-col justify-between"
      style={{ backgroundColor: "#0066B1", maxWidth: "290px", width: "100%" }}
    >
      <div className="flex flex-col items-start gap-9">
        <div className="flex flex-col items-start gap-4 w-full">
          <Link
            className="hidden lg:block relative"
            to={
              showManagementMenu
                ? localizedPath("/dashboard/admin")
                : localizedPath("/dashboard")
            }
          >
            <img
              src={logo}
              alt={intl.formatMessage({ id: "common.tereosAlt" })}
              style={{ width: "156px", height: "43px", objectFit: "contain" }}
            />
          </Link>
          <Link className="lg:hidden" to={localizedPath("/")}>
            <LogoMini />
          </Link>
        </div>
        <nav className="w-full">
          <span className="text-white text-sm font-medium tracking-wide">
            {intl.formatMessage({ id: "menu.section" })}
          </span>
          <ul className="mt-4">
            {showManagementMenu && (
              <>
                <li className="mb-2.5">
                  <MenuLink
                    path={localizedPath("/dashboard/admin")}
                    label={intl.formatMessage({ id: "menu.dashboard" })}
                    icon={<DashboardIcon className="w-5 h-5" />}
                    end
                  />
                </li>
                <li className="mb-2.5">
                  <MenuLink
                    path={localizedPath("/dashboard/admin/objects")}
                    label={intl.formatMessage({ id: "menu.equipmentList" })}
                    icon={<EquipmentIcon className="w-5 h-5" />}
                    excludePaths={[
                      localizedPath("/dashboard/admin/objects/import-template"),
                    ]}
                  />
                </li>
                {showImportTemplateMenu && (
                  <li className="mb-2.5">
                    <MenuLink
                      path={localizedPath("/dashboard/admin/objects/import-template")}
                      label={intl.formatMessage({ id: "menu.importTemplate" })}
                      icon={<DownloadIcon className="w-5 h-5" />}
                    />
                  </li>
                )}
                {showUsersMenu && (
                  <li className="mb-2.5">
                    <MenuLink
                      path={localizedPath("/dashboard/admin/users")}
                      label={intl.formatMessage({ id: "menu.users" })}
                      icon={<UsersIcon className="w-5 h-5" />}
                    />
                  </li>
                )}
                {showSitesMenu && (
                  <li className="mb-2.5">
                    <MenuLink
                      path={localizedPath("/dashboard/admin/sites")}
                      label={intl.formatMessage({ id: "menu.sites" })}
                      icon={<SettingIcon className="w-5 h-5" />}
                    />
                  </li>
                )}
                {isAdmin() && (
                  <li className="mb-2.5">
                    <MenuLink
                      path={localizedPath("/dashboard/admin/addresses")}
                      label={intl.formatMessage({ id: "menu.addresses" })}
                      icon={<LinkIcon className="w-5 h-5" />}
                    />
                  </li>
                )}
              </>
            )}
          </ul>
        </nav>
      </div>

      <div className="w-full flex flex-col gap-2.5">
        {showManagementMenu && (
          <MenuLink
            path={localizedPath("/dashboard/profile")}
            label={intl.formatMessage({ id: "menu.myAccount" })}
            icon={<ProfileIcon className="w-5 h-5" />}
          />
        )}
        <button
          type="button"
          onClick={() => removeAuth()}
          className="py-2 md:py-3.5 px-3 md:px-5 max-w-fit lg:max-w-full flex gap-3 items-center rounded-md text-sm text-white hover:bg-white"
          style={{ backgroundColor: "#004697" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#FFFFFF";
            e.currentTarget.style.color = "#0066B1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#004697";
            e.currentTarget.style.color = "#FFFFFF";
          }}
        >
          <LogoutIcon />
          <span className="hidden lg:block">
            {intl.formatMessage({ id: "menu.logout" })}
          </span>
        </button>
      </div>
    </div>
  );
};

export default SideMenu;
